/**
 * Où poser le clavier virtuel pour qu'il ne cache pas ce qu'on est en train de taper.
 *
 * Signalé par Philippe (25/09/2026) : en caisse, le pavé numérique se posait sur
 * le champ du poids libre et sur celui du montant au paiement — impossible de voir
 * ce qu'on écrivait. Le clavier se plaçait sans jamais regarder le champ : toujours
 * en bas à droite de l'écran (ou à la dernière position où on l'avait traîné), et
 * avec les dimensions du grand clavier AZERTY même quand il affichait le petit pavé.
 * Or ces deux champs vivent au milieu d'une fenêtre centrée : pile dessous.
 *
 * La règle, pure et testée : on essaie d'éviter les ZONES dans l'ordre donné — la
 * fenêtre de saisie entière d'abord (pour ne masquer ni le champ ni son bouton de
 * validation), le champ seul ensuite. Pour chaque zone : à droite, à gauche,
 * dessous, dessus. Si rien ne tient, on prend la position qui chevauche le MOINS
 * le champ. La position choisie par le caissier en traînant le clavier est gardée
 * tant qu'elle ne cache pas la première zone.
 */

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Taille {
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

const MARGE = 12;

function rectDe(p: Point, clavier: Taille): Rect {
  return { left: p.x, top: p.y, right: p.x + clavier.w, bottom: p.y + clavier.h };
}

function surfaceCommune(a: Rect, b: Rect): number {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

function borne(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(v, Math.max(min, max)));
}

/** Les quatre emplacements autour d'une zone, dans l'ordre de préférence. */
function autourDe(zone: Rect, clavier: Taille, ecran: Taille): Point[] {
  const maxX = ecran.w - clavier.w - MARGE;
  const maxY = ecran.h - clavier.h - MARGE;
  const cy = borne((zone.top + zone.bottom) / 2 - clavier.h / 2, MARGE, maxY);
  const cx = borne((zone.left + zone.right) / 2 - clavier.w / 2, MARGE, maxX);
  return [
    { x: zone.right + MARGE, y: cy }, // à droite
    { x: zone.left - MARGE - clavier.w, y: cy }, // à gauche
    { x: cx, y: zone.bottom + MARGE }, // dessous
    { x: cx, y: zone.top - MARGE - clavier.h }, // dessus
  ];
}

function tientALEcran(p: Point, clavier: Taille, ecran: Taille): boolean {
  return (
    p.x >= MARGE - 0.5 &&
    p.y >= MARGE - 0.5 &&
    p.x + clavier.w <= ecran.w - MARGE + 0.5 &&
    p.y + clavier.h <= ecran.h - MARGE + 0.5
  );
}

/**
 * @param zones   Zones à ne pas couvrir, de la plus large (la fenêtre) à la plus
 *                étroite (le champ). La dernière est celle qu'on protège coûte que coûte.
 * @param souhait Position préférée (celle où le caissier a laissé le clavier).
 */
export function placerClavier(zones: Rect[], clavier: Taille, ecran: Taille, souhait?: Point | null): Point {
  if (!zones.length) {
    return souhait ?? { x: borne(ecran.w - clavier.w - MARGE, MARGE, Infinity), y: borne(ecran.h - clavier.h - MARGE, MARGE, Infinity) };
  }

  if (souhait && tientALEcran(souhait, clavier, ecran) && surfaceCommune(rectDe(souhait, clavier), zones[0]) === 0) {
    return souhait;
  }

  for (const zone of zones) {
    for (const p of autourDe(zone, clavier, ecran)) {
      if (tientALEcran(p, clavier, ecran) && surfaceCommune(rectDe(p, clavier), zone) === 0) return p;
    }
  }

  // Rien ne tient sans chevauchement : la position qui cache le moins le champ,
  // ramenée dans l'écran.
  const champ = zones[zones.length - 1];
  const candidats = autourDe(champ, clavier, ecran).map((p) => ({
    x: borne(p.x, MARGE, ecran.w - clavier.w - MARGE),
    y: borne(p.y, MARGE, ecran.h - clavier.h - MARGE),
  }));
  candidats.sort((a, b) => surfaceCommune(rectDe(a, clavier), champ) - surfaceCommune(rectDe(b, clavier), champ));
  return candidats[0];
}

/**
 * « Tout doit rester visible quand le clavier s'ouvre » (Philippe, 21/10/2026).
 *
 * `placerClavier` évite ce qu'il peut, mais le grand clavier AZERTY (1200 × 680)
 * ne tient presque jamais À CÔTÉ d'une fenêtre centrée sur un écran de 1920 × 1080 :
 * il retombait sur la fenêtre et en cachait les autres champs et le bouton de
 * validation. Plutôt que de cacher, on fait de la place :
 *
 * - champ dans une FENÊTRE : si le clavier peut se poser sans la couvrir, rien ne
 *   bouge ; sinon il se range dans le coin bas de son côté (droit par défaut, gauche
 *   si le caissier l'y a laissé) et la fenêtre glisse dans la place restante. Trop
 *   large pour y tenir, elle s'aligne sur le bord de l'écran, et remonte au besoin
 *   juste assez pour que le champ en saisie reste visible ;
 * - champ dans une PAGE : le clavier se range de même et la page se RESSERRE à
 *   côté de lui (`reserve`) — toute la page reste lisible, résultats compris ;
 * - champ de la zone de vente (recherche de la caisse) : comportement d'avant,
 *   le clavier évite le champ et l'écran de vente ne bouge pas.
 */
export type Cote = 'gauche' | 'droite';

export interface PlanClavier {
  clavier: Point;
  /** Translation à appliquer à la fenêtre qui contient le champ. */
  decalage: Point;
  /** Place à laisser au clavier sur un côté de la page, ou null. */
  reserve: { cote: Cote; largeur: number } | null;
}

export function planClavier(p: {
  champ: Rect;
  /** Fenêtre (boîte de dialogue) qui contient le champ, ou null pour un champ de page. */
  boite: Rect | null;
  /** Champ de page dans la zone de vente (recherche de la caisse) : la page ne bouge pas. */
  vente?: boolean;
  clavier: Taille;
  ecran: Taille;
  souhait?: Point | null;
}): PlanClavier {
  const { champ, boite, clavier, ecran, souhait } = p;
  const rien = { x: 0, y: 0 };
  if (!boite && p.vente) {
    return { clavier: placerClavier([champ], clavier, ecran, souhait), decalage: rien, reserve: null };
  }
  const cote: Cote = souhait && souhait.x + clavier.w / 2 < ecran.w / 2 ? 'gauche' : 'droite';
  const coin: Point = {
    x: cote === 'droite' ? borne(ecran.w - clavier.w - MARGE, MARGE, Infinity) : MARGE,
    y: borne(ecran.h - clavier.h - MARGE, MARGE, Infinity),
  };

  if (!boite) {
    return { clavier: coin, decalage: rien, reserve: { cote, largeur: clavier.w + 2 * MARGE } };
  }

  // 1. Le clavier tient-il à côté de la fenêtre sans rien déplacer ?
  const libre = placerClavier([boite, champ], clavier, ecran, souhait);
  if (surfaceCommune(rectDe(libre, clavier), boite) === 0) {
    return { clavier: libre, decalage: rien, reserve: null };
  }

  // 2. La fenêtre glisse dans la place que laisse le clavier rangé dans son coin.
  const gauche = cote === 'droite' ? MARGE : coin.x + clavier.w + MARGE;
  const droite = cote === 'droite' ? coin.x - MARGE : ecran.w - MARGE;
  const large = boite.right - boite.left;
  const cible =
    large <= droite - gauche ? gauche + (droite - gauche - large) / 2 : cote === 'droite' ? gauche : droite - large;
  const dx = Math.round(cible - boite.left);

  // 3. Fenêtre trop large : le champ ne doit pas finir sous le clavier.
  const champDeplace: Rect = { left: champ.left + dx, right: champ.right + dx, top: champ.top, bottom: champ.bottom };
  let dy = 0;
  if (surfaceCommune(champDeplace, rectDe(coin, clavier)) > 0) {
    dy = Math.min(0, Math.round(coin.y - MARGE - champ.bottom));
  }
  return { clavier: coin, decalage: { x: dx, y: dy }, reserve: null };
}
